import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Select, Space } from 'antd';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import SupplierContactsFormList from './SupplierContactsFormList';
import {
  normalizeSupplierContacts,
  toSupplierContactFormList
} from '../../utils/supplierContacts';
import type { Supplier, SupplierFormValues, SupplierSubmitValues } from '../../modules/suppliers/types';
import { useFeedback } from '../../utils/feedback';

const CURRENCIES = ['USD', 'EUR', 'PLN', 'GBP', 'CNY'];

interface SupplierFormProps {
  form: FormInstance<SupplierFormValues>;
  editingSupplier: Supplier | null;
  submitting: boolean;
  submitLabel: string;
  onSubmit: (values: SupplierSubmitValues) => void | Promise<void>;
  /** Re-initialize fields when this flips to true (modal open / entering edit). */
  open?: boolean;
  onCancel?: () => void;
}

export function buildSupplierSubmitValues(values: SupplierFormValues): SupplierSubmitValues {
  const contactPeople = normalizeSupplierContacts(values.contactPeople);
  const primaryContact = contactPeople[0] || null;

  return {
    name: values.name,
    contactPeople,
    contactPerson: primaryContact?.name || null,
    email: primaryContact?.email || null,
    phone: primaryContact?.phone || null,
    taxId: values.taxId || null,
    address: values.address || null,
    defaultCurrency: values.defaultCurrency || 'PLN'
  };
}

/**
 * Shared supplier create/edit form. Owns NIP lookup and contact-list wiring;
 * consumers (list create modal, detail page) only supply `editingSupplier`
 * and handle the API call + field-level error mapping on their shared `form`
 * instance in `onSubmit`.
 */
const SupplierForm: React.FC<SupplierFormProps> = ({
  form,
  editingSupplier,
  submitting,
  submitLabel,
  onSubmit,
  open = true,
  onCancel
}) => {
  const { t } = useTranslation(['suppliers', 'common']);
  const feedback = useFeedback();
  const [nipFetching, setNipFetching] = useState(false);

  const editingPrimaryContact = editingSupplier
    ? normalizeSupplierContacts(
        editingSupplier.contactPeople,
        editingSupplier.contactPerson,
        editingSupplier.phone,
        editingSupplier.email
      )[0] || null
    : null;
  const allowBlankPrimaryName = Boolean(
    editingPrimaryContact &&
    !editingPrimaryContact.name &&
    (editingPrimaryContact.phone || editingPrimaryContact.email)
  );

  // Initialize form fields from the supplier being edited (or blanks for a
  // new supplier) whenever the form is (re)opened.
  useEffect(() => {
    if (!open) return;

    if (editingSupplier) {
      form.setFieldsValue({
        name: editingSupplier.name,
        contactPeople: toSupplierContactFormList(
          editingSupplier.contactPeople,
          editingSupplier.contactPerson,
          editingSupplier.phone,
          editingSupplier.email
        ),
        taxId: editingSupplier.taxId || '',
        address: editingSupplier.address || '',
        defaultCurrency: editingSupplier.defaultCurrency || undefined
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ contactPeople: toSupplierContactFormList() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingSupplier]);

  const handleNipLookup = async () => {
    const taxId = form.getFieldValue('taxId');
    if (!taxId) {
      feedback.warning(t('suppliers:messages.nipEnterFirst'));
      return;
    }

    setNipFetching(true);
    try {
      const response = await api.post('/suppliers/lookup-nip', { nip: taxId });
      const data = response.data;

      form.setFieldsValue({
        name: data.name,
        address: data.address || ''
      });

      if (data.nip) {
        form.setFieldValue('taxId', data.nip);
      }

      feedback.success(t('suppliers:messages.nipLookupSuccess', { name: data.name || '' }));
    } catch (error: any) {
      const code = error.response?.data?.code;
      if (code === 'NIP_INVALID') {
        feedback.error(t('suppliers:messages.nipInvalid'));
      } else if (code === 'NIP_NOT_FOUND') {
        feedback.warning(t('suppliers:messages.nipNotFound'));
      } else {
        feedback.error(t('suppliers:messages.nipLookupFailed'));
      }
    } finally {
      setNipFetching(false);
    }
  };

  const handleFinish = (values: SupplierFormValues) => {
    onSubmit(buildSupplierSubmitValues(values));
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      onFinishFailed={() => feedback.warning(t('suppliers:messages.fillRequired'))}
      scrollToFirstError
    >
      <Form.Item
        name="name"
        label={t('suppliers:form.name')}
        rules={[{ required: true, message: t('suppliers:validation.nameRequired') }]}
      >
        <Input />
      </Form.Item>
      <SupplierContactsFormList
        addLabel={t('suppliers:form.addContact')}
        allowBlankPrimaryName={allowBlankPrimaryName}
        contactLabel={t('suppliers:form.contact')}
        emailLabel={t('suppliers:form.contactEmail')}
        emailRequiredMessage={t('suppliers:validation.contactEmailRequired')}
        nameLabel={t('suppliers:form.contactName')}
        nameRequiredMessage={t('suppliers:validation.contactNameRequired')}
        phoneLabel={t('suppliers:form.contactPhone')}
        phoneRequiredMessage={t('suppliers:validation.contactPhoneRequired')}
        removeLabel={t('suppliers:form.removeContact')}
      />
      <Form.Item
        name="taxId"
        label={t('suppliers:form.taxId')}
      >
        <Input.Search
          placeholder={t('suppliers:form.enterTaxId')}
          enterButton={t('suppliers:form.lookupNip')}
          loading={nipFetching}
          onSearch={handleNipLookup}
        />
      </Form.Item>
      <Form.Item
        name="defaultCurrency"
        label={t('suppliers:form.defaultCurrency')}
      >
        <Select
          allowClear
          placeholder="—"
          getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
        >
          {CURRENCIES.map((currency) => (
            <Select.Option key={currency} value={currency}>
              {currency}
            </Select.Option>
          ))}
        </Select>
      </Form.Item>
      <Form.Item
        name="address"
        label={t('suppliers:form.address')}
      >
        <Input.TextArea rows={3} />
      </Form.Item>
      <Form.Item>
        <Space>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {submitLabel}
          </Button>
          {onCancel && (
            <Button onClick={onCancel} disabled={submitting}>
              {t('common:cancel')}
            </Button>
          )}
        </Space>
      </Form.Item>
    </Form>
  );
};

export default SupplierForm;
